/**
 * Image Upload — browser helper.
 *
 * @api-version 1
 * @contract    Bliss/_shared/UPLOAD_CONTRACT.md
 *
 * ⚠️ THIS IS A STAGED COPY. DO NOT EDIT HERE.
 * Canonical source: Bliss/_shared/uploadHelper.js
 * Edits made to this copy will be overwritten on the next Harmony stage step.
 *
 * Uploads a batch of files via the presigned-PUT flow:
 *   1. POST /api/get-upload-urls   → list of presigned URLs + S3 keys
 *   2. For each file, PUT bytes directly to S3 (parallelism-bounded)
 *   3. Return the resolved S3 keys + per-file failures so the caller can
 *      send keys to the process endpoint.
 *
 * Imported by tool-specific API modules (e.g. imageStudioApi.js). Tools wrap
 * this helper, then post `image_keys` to /api/process.
 *
 * MIGRATION NOTE
 * ---------------
 * This file is the canonical implementation. Harmony bundles JS at build
 * time so import paths resolve at integration time — `imageStudioApi.js`
 * imports it as `'../../_shared/uploadHelper.js'`. When SmartBiz packaging
 * arrives, this becomes `@smartbiz/image-upload-client` and the import path
 * is the only thing that changes.
 */

import { apiFetch } from '../../lib/apiFetch.js';

const DEFAULT_PUT_CONCURRENCY = 4;
const DEFAULT_PUT_RETRIES = 1;

// ── Internal helpers ──

async function mapWithConcurrency(items, concurrency, fn) {
  const results = new Array(items.length);
  let nextIndex = 0;
  async function worker() {
    while (true) {
      const i = nextIndex++;
      if (i >= items.length) return;
      results[i] = await fn(items[i], i);
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, worker);
  await Promise.all(workers);
  return results;
}

/** Fetch wrapper that surfaces a usable error even when the response is HTML / empty. */
async function safeJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function putWithRetry(url, file, contentType, retries) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (res.ok) return { ok: true };
      // 4xx is not retryable (signature expired, content-type mismatch, etc.)
      if (res.status >= 400 && res.status < 500) {
        return { ok: false, error: `S3 PUT ${res.status}: ${res.statusText || 'rejected'}` };
      }
      lastErr = new Error(`S3 PUT ${res.status}: ${res.statusText || 'server error'}`);
    } catch (err) {
      lastErr = err;
    }
  }
  return { ok: false, error: lastErr ? lastErr.message : 'unknown error' };
}

// ── Public API ──

/**
 * Upload a batch of files via presigned PUTs.
 *
 * @param {Object} opts
 * @param {File[]} opts.files            — browser File objects
 * @param {string} opts.apiBaseUrl       — e.g. https://...execute-api...
 * @param {string} opts.runId
 * @param {string} opts.operation        — "resize" | "convert" | "background"
 * @param {number} [opts.concurrency=4]  — parallel PUTs
 * @param {number} [opts.retries=1]      — PUT retries on 5xx / network
 * @param {function} [opts.onProgress]   — ({ uploaded, total, phase }) => void
 *                                          phases: 'presigning', 'uploading'
 *
 * @returns {Promise<{
 *   keys: string[],                                       // s3_keys, in input order; empty string if failed
 *   uploads: Array<{ name, s3_key, ok, error?, status }>, // per-file detail
 *   failures: Array<{ name, error }>                      // shortcut for the failed entries
 * }>}
 *
 * Throws only on the presign call failing entirely (network error or non-2xx).
 * Per-file PUT failures are returned in `uploads` with `ok: false`.
 */
export async function uploadFilesViaPresignedUrls({
  files,
  apiBaseUrl,
  runId,
  operation,
  concurrency = DEFAULT_PUT_CONCURRENCY,
  retries = DEFAULT_PUT_RETRIES,
  onProgress,
}) {
  if (!Array.isArray(files) || files.length === 0) {
    return { keys: [], uploads: [], failures: [] };
  }
  if (!apiBaseUrl) throw new Error('apiBaseUrl is required');
  if (!runId) throw new Error('runId is required');
  if (!operation) throw new Error('operation is required');

  const total = files.length;
  if (onProgress) onProgress({ uploaded: 0, total, phase: 'presigning' });

  // 1. Presign
  const presignRes = await apiFetch(`${apiBaseUrl}/api/get-upload-urls`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      run_id: runId,
      operation,
      files: files.map((f) => ({ name: f.name, size: f.size })),
    }),
  });
  if (!presignRes.ok) {
    const err = await safeJson(presignRes);
    throw new Error(
      (err && err.error) ||
      `Presign failed: HTTP ${presignRes.status} ${presignRes.statusText || ''}`.trim()
    );
  }
  const presignBody = await presignRes.json();
  const presigned = Array.isArray(presignBody.uploads) ? presignBody.uploads : [];

  // Build a name → presign-entry map. Server-side sanitises names, so the
  // client matches by trying both the original and the sanitised forms.
  const byOriginal = new Map(files.map((f) => [f.name, f]));

  // 2. PUTs (bounded concurrency)
  if (onProgress) onProgress({ uploaded: 0, total, phase: 'uploading' });
  let uploadedCount = 0;

  const uploads = await mapWithConcurrency(presigned, concurrency, async (entry) => {
    // If this entry failed at presign time, surface that and skip the PUT.
    if (entry.status !== 'ready' || !entry.upload_url) {
      uploadedCount += 1;
      if (onProgress) onProgress({ uploaded: uploadedCount, total, phase: 'uploading' });
      return {
        name: entry.name,
        s3_key: entry.s3_key || '',
        ok: false,
        error: entry.status || 'no upload URL',
        status: entry.status || 'Failed: no upload URL',
      };
    }

    // Find the matching File. The server may have sanitised the name.
    const file =
      byOriginal.get(entry.name) ||
      // Best-effort match: same extension and our sanitisation matches
      files.find((f) => sanitiseLike(f.name) === entry.name);

    if (!file) {
      uploadedCount += 1;
      if (onProgress) onProgress({ uploaded: uploadedCount, total, phase: 'uploading' });
      return {
        name: entry.name,
        s3_key: entry.s3_key,
        ok: false,
        error: 'no matching local file',
        status: 'Failed: no matching local file',
      };
    }

    const result = await putWithRetry(entry.upload_url, file, entry.content_type, retries);
    uploadedCount += 1;
    if (onProgress) onProgress({ uploaded: uploadedCount, total, phase: 'uploading' });

    return {
      name: entry.name,
      s3_key: entry.s3_key,
      ok: result.ok,
      error: result.ok ? undefined : result.error,
      status: result.ok ? 'ok' : `Failed: ${result.error}`,
    };
  });

  const keys = uploads.map((u) => (u.ok ? u.s3_key : ''));
  const failures = uploads
    .filter((u) => !u.ok)
    .map((u) => ({ name: u.name, error: u.error || 'unknown' }));

  return { keys, uploads, failures };
}

/**
 * Mirror of the server-side `sanitizeFilename`. Kept in sync manually for now.
 * Browser callers don't normally need this; it's exposed for the rare case
 * where the server sanitised a name and we need to find the matching File.
 */
export function sanitiseLike(name) {
  if (!name) return '';
  const base = name.replace(/\\/g, '/').split('/').pop() || '';
  return base.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 200);
}
